/**
 * WHO MAY HOLD A STAFF LOGIN, AND WHAT THEIR PASSWORD MUST BE.
 *
 *   "the login must be hard wired to only accept @orchidholdings.co.za emails
 *    and unique passwords, minimum 8 alphanumeric with special character and
 *    Caps."
 *
 * WHERE THE REAL ENFORCEMENT LIVES
 *
 * Not here. The domain rule is a trigger on admin_users
 * (fn_admin_email_must_be_orchid, script 48), because a check living only in a
 * React component is a check a curl request skips. This module exists so that
 * somebody typing the wrong address is told immediately and in plain language
 * rather than being handed a 23514 from Postgres — it is courtesy, and the
 * database is the rule.
 *
 * The password policy is different, and worth being precise about: Supabase
 * Auth holds the credential, the browser never sends it here, and no trigger of
 * ours ever sees it. So this IS the enforcement point for complexity, which is
 * why it is a pure function with tests rather than a regex inlined in a form —
 * and why the project's own "Minimum password requirements" setting should be
 * raised to match, as belt and braces. See README.
 *
 * ABOUT "UNIQUE PASSWORDS"
 *
 * Cross-account uniqueness — "no two staff share a password" — cannot be
 * enforced and should not be attempted. Supabase stores bcrypt hashes with a
 * per-password salt, so two identical passwords produce two different hashes
 * and no comparison is possible. Making it possible would mean storing
 * something unsalted alongside, which converts a leaked database from "hashes
 * an attacker must crack one by one" into "a lookup table". That is a strictly
 * worse system than the one the requirement was meant to improve.
 *
 * What IS achievable, and is done below, is the part of "unique" that actually
 * prevents the attack people have in mind: a password that is not derived from
 * the person's own name or address, and is not one of the handful of strings
 * that appear at the top of every breach corpus.
 */

export const STAFF_DOMAIN = 'orchidholdings.co.za';

/**
 * Is this an Orchid staff address?
 *
 * Deliberately strict about the suffix: 'someone@notorchidholdings.co.za' ends
 * with the domain as a SUBSTRING, and an `endsWith` check would let it in. The
 * '@' is part of what is matched for exactly that reason.
 */
export function isStaffEmail(value) {
  const email = String(value || '').trim().toLowerCase();
  if (!email) return false;
  // One '@', something before it, and our domain exactly after it.
  const parts = email.split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  return local.length > 0 && domain === STAFF_DOMAIN;
}

export function emailProblem(value) {
  const email = String(value || '').trim();
  if (!email) return 'Enter your Orchid email address.';
  if (!isStaffEmail(email)) {
    return `Staff sign-in accepts @${STAFF_DOMAIN} addresses only.`;
  }
  return null;
}

/**
 * The five rules a staff password must satisfy, each answerable on its own so
 * the form can show which are met while somebody types rather than rejecting
 * the whole thing at the end with one sentence.
 */
export const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'upper', label: 'A capital letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { key: 'digit', label: 'A number', test: (v) => /[0-9]/.test(v) },
  {
    key: 'special',
    label: 'A special character',
    // Anything that is not a letter or a digit, rather than a list of
    // punctuation. A list always omits somebody's keyboard.
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

/**
 * Strings that are not passwords, in the sense that matters: they are in every
 * corpus an attacker already has, so their length and character mix are beside
 * the point. Short list on purpose — a long one belongs in a service, and
 * pretending a 40-entry array is breach coverage would be the more dangerous
 * mistake.
 */
const KNOWN_WEAK = [
  'password1!', 'password123!', 'passw0rd!', 'welcome1!', 'welcome123!',
  'qwerty123!', 'admin123!', 'letmein1!', 'orchid123!', 'changeme1!',
  'abcd1234!', 'p@ssword1', 'p@ssw0rd', 'iloveyou1!', 'monkey123!',
];

/**
 * Everything wrong with this password, in the order a person should fix it.
 *
 * @param {string} password
 * @param {{email?: string, fullName?: string}} identity  what this password
 *        must not be made of. Passing nothing skips those checks rather than
 *        failing them — a caller that has not collected a name yet is not
 *        thereby holding a bad password.
 * @returns {{ok: boolean, unmet: string[], problems: string[]}}
 */
export function passwordProblems(password, identity = {}) {
  const value = String(password || '');
  const unmet = PASSWORD_RULES.filter((r) => !r.test(value)).map((r) => r.key);
  const problems = [];

  if (unmet.length) {
    problems.push(
      'This password needs ' +
        PASSWORD_RULES.filter((r) => unmet.includes(r.key))
          .map((r) => r.label.toLowerCase())
          .join(', ') +
        '.'
    );
  }

  const lower = value.toLowerCase();

  if (KNOWN_WEAK.includes(lower)) {
    problems.push('This is one of the most commonly tried passwords there is. Choose another.');
  }

  // A password made of the address it signs in with is the one an attacker
  // guesses first, and it satisfies every complexity rule above.
  const local = String(identity.email || '').split('@')[0].toLowerCase();
  if (local.length >= 4 && lower.includes(local)) {
    problems.push('A password must not contain your email address.');
  }

  const names = String(identity.fullName || '')
    .toLowerCase()
    .split(/\s+/)
    .filter((n) => n.length >= 4);
  if (names.some((n) => lower.includes(n))) {
    problems.push('A password must not contain your own name.');
  }

  // Not a complexity rule: 'Aaaaaaa1!' passes all five and is one guess.
  if (value.length >= 8 && new Set(value).size < 5) {
    problems.push('This password repeats too few different characters.');
  }

  return { ok: problems.length === 0, unmet, problems };
}

/**
 * Which rules are currently met — for the live checklist under the field.
 * Shown as progress rather than as errors: a half-typed password is not a
 * mistake, and colouring it red while somebody is still typing teaches them to
 * ignore the colour.
 */
export function ruleState(password) {
  const value = String(password || '');
  return PASSWORD_RULES.map((r) => ({ key: r.key, label: r.label, met: r.test(value) }));
}
